export interface FigmaClipboardFrame {
  fileKey: string;
  nodeId: string;
}

export interface FigmaClipboardSelection {
  frames: FigmaClipboardFrame[];
  hasUnsupportedNodes: boolean;
  /** `window.location.hostname` of the Figma tab that copied, when present. */
  environment?: string;
}

export interface FigmaClipboardEnvironmentMismatch {
  sourceHost: string;
  expectedHost: string;
}

const FIGMA_PROD_HOSTS = new Set(['figma.com', 'www.figma.com']);

function hostnameFrom(value?: string): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  try {
    return new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`).hostname.toLowerCase();
  } catch {
    return undefined;
  }
}

function displayFigmaHost(hostname: string): string {
  return hostname === 'www.figma.com' ? 'figma.com' : hostname;
}

/** Cross-product: copied Figma host vs this Weave build's Figma origin (`VITE_FIGMA_BASE_URL`). */
export function isFigmaClipboardEnvironmentMismatch(
  clipboardEnvironment: string | undefined,
  figmaBaseUrl: string,
): FigmaClipboardEnvironmentMismatch | undefined {
  const sourceHost = hostnameFrom(clipboardEnvironment);
  const expectedHost = hostnameFrom(figmaBaseUrl);
  if (!sourceHost || !expectedHost) return undefined;
  if (FIGMA_PROD_HOSTS.has(sourceHost) === FIGMA_PROD_HOSTS.has(expectedHost)) return undefined;
  return { sourceHost: displayFigmaHost(sourceHost), expectedHost: displayFigmaHost(expectedHost) };
}

const META_START = '<!--(figmeta)';
const META_END = '(/figmeta)-->';
const ESCAPED_META_START = '&lt;!--(figmeta)';
const ESCAPED_META_END = '(/figmeta)--&gt;';
// FGFakeClipboard writes `static_cast<int>(node->type())` from nodetype.figidl.
const FIGMA_FRAME_NODE_TYPE = '4';
const FIGMA_TEXT_NODE_TYPE = '31';

interface FigmaPastedMetadata {
  fileKey: string;
  selectedNodeData: string;
  environment?: unknown;
}

type ParsedNode = { nodeId: string; nodeType: string; isLocked: boolean; isGroup: boolean };

function findMetadataPayloadBase64(html: string): string | null {
  let startIdx = html.indexOf(META_START);
  let endIdx = html.indexOf(META_END);
  let startLen = META_START.length;

  // Cmd+C inserts unescaped chars; right-click → "Copy" goes through the
  // navigator clipboard API, which HTML-escapes them.
  if (startIdx < 0 || endIdx < 0) {
    startIdx = html.indexOf(ESCAPED_META_START);
    endIdx = html.indexOf(ESCAPED_META_END);
    startLen = ESCAPED_META_START.length;
  }
  if (startIdx < 0 || endIdx < 0) return null;

  return html.slice(startIdx + startLen, endIdx);
}

function decodeMetadata(base64: string): FigmaPastedMetadata | null {
  try {
    const json = decodeURIComponent(escape(atob(base64)));
    return JSON.parse(json) as FigmaPastedMetadata;
  } catch {
    return null;
  }
}

/**
 * Plain frames become Figma nodes. Top-level text layers return empty so paste
 * falls through to text/plain. Images and groups are unsupported (Copy as PNG
 * still hits the clipboard image path first).
 */
export function parseFigmaSelectionFromClipboardHtml(html: string): FigmaClipboardSelection {
  const emptySelection: FigmaClipboardSelection = { frames: [], hasUnsupportedNodes: false };
  const base64 = findMetadataPayloadBase64(html);
  if (!base64) return emptySelection;

  const metadata = decodeMetadata(base64);
  if (!metadata) return emptySelection;

  const { fileKey, selectedNodeData } = metadata;
  if (!fileKey || !selectedNodeData) return emptySelection;

  const environment = typeof metadata.environment === 'string' ? metadata.environment.trim() : '';
  const withEnvironment = environment ? { environment } : {};

  const nodes = selectedNodeData
    .split(',')
    .map((entry) => {
      // nodeId|nodeType|lockedFlag[|isGroupFlag] — isGroupFlag is optional for older Figma builds.
      const [nodeId, nodeType, lockedFlag, isGroupFlag] = entry.split('|');
      return { nodeId, nodeType, isLocked: lockedFlag === '1', isGroup: isGroupFlag === '1' };
    })
    .filter((node) => node.nodeId && !node.isLocked);

  if (nodes.length === 0) return { ...emptySelection, ...withEnvironment };

  if (nodes.every((node) => node.nodeType === FIGMA_TEXT_NODE_TYPE)) {
    return { ...emptySelection, ...withEnvironment };
  }

  // Plain frames only: modern Figma groups are still type FRAME, distinguished by isGroup.
  const isSupportedFrame = (node: ParsedNode) => node.nodeType === FIGMA_FRAME_NODE_TYPE && !node.isGroup;

  return {
    frames: nodes.filter(isSupportedFrame).map(({ nodeId }) => ({ fileKey, nodeId })),
    hasUnsupportedNodes: nodes.some((node) => !isSupportedFrame(node)),
    ...withEnvironment,
  };
}
